import { ChevronDownIcon } from '@chakra-ui/icons'
import {
  AspectRatio,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Skeleton,
  Text,
} from '@chakra-ui/react'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useTranslation } from 'react-i18next'
import { FiChevronDown } from 'react-icons/fi'
import useFetch, { CachePolicies } from 'use-http'
import { PermissionsEnum } from '../../../../utils'
import { filesPerPage } from '../../../../utils/mediaLibrary'
import { isRestrictedUser } from '../../../../utils/roles'
import { useQueryState } from '../../../hooks/useQueryState'
import useToast from '../../../hooks/useToast'
import {
  GenerateImageResponse,
  ListAllActiveUsersResponse,
} from '../../../interfaces'
import { UserContext } from '../../../providers'
import ImageGallery from './ImageGallery'
import MultiSelectMenu from './MultiSelectMenu'

const DateInput = (
  inputProps: React.HTMLProps<HTMLInputElement>,
  ref: React.Ref<HTMLInputElement>,
): React.ReactElement => {
  const { value, onClick, onChange } = inputProps

  return (
    <InputGroup>
      <Input
        cursor="pointer"
        minW="137px"
        minH="35px"
        bgColor="white"
        value={value || 'Date created'}
        onClick={onClick}
        onChange={onChange}
        placeholder="Pick a date"
        ref={ref}
        readOnly
      />
      {!value && (
        <InputRightElement pointerEvents="none">
          <ChevronDownIcon />
        </InputRightElement>
      )}
    </InputGroup>
  )
}

const GenerateImageHistory = () => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const toast = useToast()

  const [users, setUsers] = useState<ListAllActiveUsersResponse[]>([])
  const [generatedImages, setGeneratedImages] = useState<
    GenerateImageResponse[]
  >([])
  const [filePage, setFilePage] = useState(0)
  const [skip, setSkip] = useState(0)
  const [limit] = useState(100)
  const [loading, setLoading] = useState(false)
  const [loadingResults, setLoadingResults] = useState(true)
  const [showNoResults, setShowNoResults] = useState(false)

  const [prompt, setPrompt] = useQueryState<string>('', 'prompt')
  const [startDate, setStartDate, updateMultipleQueryParams] =
    useQueryState<null | Date>(null, 'startDate')
  const [endDate, setEndDate] = useQueryState<null | Date>(null, 'endDate')
  const [selectedUserIds, setSelectedUserIds] = useQueryState<string[]>(
    [],
    'userIds',
  )

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const { get, loading: loadingImages } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const fetchGeneratedImages = useCallback(
    async (currentSkip, append = false) => {
      const apiQuery = new URLSearchParams()

      if (prompt) {
        apiQuery.set('prompt', prompt)
      }
      if (startDate) {
        apiQuery.set('startDate', startDate.toISOString())
      }
      if (endDate) {
        apiQuery.set('endDate', endDate.toISOString())
      }
      if (selectedUserIds.length > 0) {
        apiQuery.set('userIds', selectedUserIds.join(','))
      }

      apiQuery.set('skip', currentSkip.toString())
      apiQuery.set('limit', limit.toString())

      const response = (await get(
        `/api/media-library/${
          user?.clientNumber
        }/generate-image?${apiQuery.toString()}`,
      )) as GenerateImageResponse[]

      setLoadingResults(false)

      if (Array.isArray(response)) {
        setGeneratedImages(prevImages =>
          append ? [...prevImages, ...response] : response,
        )
      } else if (!append) {
        setGeneratedImages([])
      }
      setShowNoResults(response?.length === 0)
      setLoading(false)
    },
    [prompt, startDate, endDate, selectedUserIds],
  )

  useEffect(() => {
    const handler = setTimeout(() => {
      refetch()
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [prompt, startDate, endDate, selectedUserIds])

  const refetch = useCallback(() => {
    setSkip(0)
    setFilePage(0)
    fetchGeneratedImages(0, false)
  }, [fetchGeneratedImages])

  const getAndSetUsers = async () => {
    if (
      isRestrictedUser(user?.roles) ||
      !user?.permissions.includes(PermissionsEnum.SELF_MEDIA_LIBRARY_VIEW)
    ) {
      return
    }

    const res = (await request.get(
      `/api/clients/${user?.clientNumber}/media-audit-logs/all-active-users`,
    )) as ListAllActiveUsersResponse[]

    if (request.error) {
      toast({
        description: t('error_in_fetching_users', 'Error in fetching users'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }

    setUsers(res)
  }

  useEffect(() => {
    if (loadingImages) {
      setLoadingResults(loadingImages)
    }
  }, [loadingImages])

  useEffect(() => {
    getAndSetUsers()
  }, [])

  return (
    <Flex p="24px" direction="column">
      <Text fontSize="16px" fontWeight="500">
        {t('history', 'History')}
      </Text>
      <Flex pt="7px" pb="14px" gap="10px">
        <Input
          width="25vw"
          placeholder={t('search', 'Search')}
          value={prompt}
          onChange={event => {
            setPrompt(event.target.value)
          }}
        />
        <Box width="16vw">
          <DatePicker
            isClearable
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={dates => {
              const [start, end] = dates
              updateMultipleQueryParams({
                startDate: start,
                endDate: end,
              })
              setStartDate(start)
              setEndDate(end)
            }}
            customInput={React.createElement(React.forwardRef(DateInput))}
          />
        </Box>
        <MultiSelectMenu
          options={users}
          selectedUserIds={selectedUserIds}
          setSelectedUserIds={setSelectedUserIds}
        />
      </Flex>
      {generatedImages.length === 0 && loadingResults && !loading ? (
        <Grid
          gridTemplateColumns={[
            'repeat(1, minmax(8px, 1fr))',
            'repeat(2, minmax(8px, 1fr))',
            'repeat(4, minmax(8px, 1fr))',
          ]}
          gap={4}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <GridItem key={index} position="relative">
              <AspectRatio ratio={1} width="100%">
                <Skeleton borderRadius="8px" />
              </AspectRatio>
            </GridItem>
          ))}
        </Grid>
      ) : (
        <>
          {generatedImages.length > 0 ? (
            <ImageGallery
              generatedImages={generatedImages}
              preview="history"
              refetch={refetch}
            />
          ) : (
            showNoResults && (
              <Center flex="1" py="50px">
                <Text fontSize="18px">
                  {t('no_images_to_show', 'No images to show')}
                </Text>
              </Center>
            )
          )}

          {(generatedImages.length % filesPerPage === 0 &&
            generatedImages.length / filesPerPage === filePage + 1) ||
          loading ? (
            <Button
              alignSelf="start"
              as="a"
              variant="link"
              cursor="pointer"
              color="appClearBlue"
              pt="10px"
              onClick={() => {
                const newSkip = skip + limit
                setLoading(true)
                setSkip(newSkip)
                setFilePage(prevPage => prevPage + 1)
                fetchGeneratedImages(newSkip, true)
              }}
              isLoading={loading}
              loadingText={t('loading', 'Loading...')}
            >
              {t('see_more', 'See More')}
              <Icon as={FiChevronDown} />
            </Button>
          ) : null}
        </>
      )}
    </Flex>
  )
}

export default GenerateImageHistory
